import React, { memo, useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Users, Mail, Shield, Briefcase, LogOut, Table2 } from "lucide-react";

const navItems = [
  { label: "Investors", icon: Users, path: "type/investor" },
  { label: "Debt", icon: Users, path: "type/debt" },
  { label: "Bankers", icon: Users, path: "type/banker" },
  { label: "Others", icon: Users, path: "type/others" },
];

const styles = {
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
  container: {
    maxWidth: '80rem',
    margin: '0 auto',
    padding: '0 1rem',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '4rem',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  logo: {
    height: '2rem',
    cursor: 'pointer',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.15s ease',
    border: 'none',
    cursor: 'pointer',
  },
  navButtonActive: {
    backgroundColor: '#1a1a1a',
    color: 'white',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
  navButtonInactive: {
    backgroundColor: 'transparent',
    color: '#475569',
  },
  navButtonHover: {
    backgroundColor: '#DEC2AA',
    color: '#1a1a1a',
  },
  divider: {
    width: '1px',
    height: '1.5rem',
    backgroundColor: '#e2e8f0',
    margin: '0 0.5rem',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    position: 'relative',
  },
  profileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.25rem',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  avatar: {
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '50%',
    backgroundColor: '#DEC2AA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    transition: 'all 0.15s ease',
    border: '2px solid transparent',
  },
  avatarHover: {
    borderColor: '#1a1a1a',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 0.5rem)',
    right: 0,
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
    minWidth: '280px',
    overflow: 'hidden',
    zIndex: 100,
  },
  dropdownHeader: {
    padding: '1rem',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: '#fafafa',
  },
  dropdownEmail: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  dropdownAvatarLarge: {
    width: '3rem',
    height: '3rem',
    borderRadius: '50%',
    backgroundColor: '#DEC2AA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1a1a1a',
  },
  dropdownEmailText: {
    flex: 1,
    overflow: 'hidden',
  },
  dropdownName: {
    fontWeight: 600,
    fontSize: '0.9375rem',
    color: '#0f172a',
    marginBottom: '0.125rem',
  },
  dropdownEmailAddress: {
    fontSize: '0.8125rem',
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownUserType: {
    fontSize: '0.75rem',
    color: '#DEC2AA',
    fontWeight: 500,
    backgroundColor: '#1a1a1a',
    padding: '0.125rem 0.5rem',
    borderRadius: '0.25rem',
    marginTop: '0.25rem',
    display: 'inline-block',
  },
  dropdownBody: {
    padding: '0.5rem',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontSize: '0.875rem',
    color: '#475569',
    transition: 'all 0.15s ease',
  },
  dropdownItemHover: {
    backgroundColor: '#f8fafc',
    color: '#0f172a',
  },
  logoutItem: {
    color: '#dc2626',
  },
  logoutItemHover: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
  },
  mobileNav: {
    borderTop: '1px solid #f1f5f9',
    padding: '0.5rem',
    overflowX: 'auto',
  },
  mobileNavLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
};

const NavButton = memo(({ isActive, onClick, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getStyle = () => {
    if (isActive) {
      return { ...styles.navButton, ...styles.navButtonActive };
    }
    if (isHovered) {
      return { ...styles.navButton, ...styles.navButtonInactive, ...styles.navButtonHover };
    }
    return { ...styles.navButton, ...styles.navButtonInactive };
  };
  
  return (
    <button
      onClick={onClick}
      style={getStyle()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </button>
  );
});

const ProfileDropdown = memo(({ auth, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitial = () => {
    if (auth?.email) {
      return auth.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        style={styles.profileButton}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{
          ...styles.avatar,
          ...(isHovered || isOpen ? styles.avatarHover : {}),
        }}>
          {getInitial()}
        </div>
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>
            <div style={styles.dropdownEmail}>
              <div style={styles.dropdownAvatarLarge}>
                {getInitial()}
              </div>
              <div style={styles.dropdownEmailText}>
                <div style={styles.dropdownName}>
                  {auth?.name || 'User'}
                </div>
                <div style={styles.dropdownEmailAddress}>
                  {auth?.email || '-'}
                </div>
                {auth?.type && (
                  <span style={styles.dropdownUserType}>
                    {auth.type}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div style={styles.dropdownBody}>
            <button
              style={{
                ...styles.dropdownItem,
                ...styles.logoutItem,
                ...(hoveredItem === 'logout' ? styles.logoutItemHover : {}),
              }}
              onMouseEnter={() => setHoveredItem('logout')}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
            >
              <LogOut style={{ width: '1.125rem', height: '1.125rem' }} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

const Navbar = memo(() => {
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('accel_auth');
    window.location.href = '/login';
  };

  return (
    <nav style={styles.nav}>
      <div style={styles.container}>
        <div style={styles.inner}>
          <div style={styles.leftSection}>
            <img 
              src="/accel-logo.png" 
              alt="Accel" 
              style={styles.logo}
              onClick={() => navigate('/dashboard/type/investor')}
            />
            
            <div className="hide-on-mobile" style={styles.navLinks}>
              {navItems.map(({ label, icon: Icon, path }) => {
                const fullPath = `/dashboard/${path}`;
                const isActive = location?.pathname === fullPath;
                
                return (
                  <NavButton
                    key={path}
                    isActive={isActive}
                    onClick={() => navigate(fullPath)}
                  >
                    <Icon style={{ width: '1rem', height: '1rem' }} />
                    {label}
                  </NavButton>
                );
              })}
              
              {auth?.type === "Admin" && (
                <>
                  <div style={styles.divider} />
                  <NavButton
                    isActive={location?.pathname === '/dashboard/type/mail-merge'}
                    onClick={() => navigate('/dashboard/type/mail-merge')}
                  >
                    <Mail style={{ width: '1rem', height: '1rem' }} />
                    Mail Merge
                  </NavButton>
                  <NavButton
                    isActive={location?.pathname?.includes('admin') && !location?.pathname?.includes('database-editor')}
                    onClick={() => navigate('/dashboard/admin/investors')}
                  >
                    <Shield style={{ width: '1rem', height: '1rem' }} />
                    Admin
                  </NavButton>
                  <NavButton
                    isActive={location?.pathname?.includes('database-editor')}
                    onClick={() => navigate('/dashboard/admin/database-editor')}
                  >
                    <Table2 style={{ width: '1rem', height: '1rem' }} />
                    DB Editor
                  </NavButton>
                </>
              )}
              
              {auth?.type === "Manager" && (
                <NavButton
                  isActive={location?.pathname?.includes('manager')}
                  onClick={() => navigate('/dashboard/manager/introductions')}
                >
                  <Briefcase style={{ width: '1rem', height: '1rem' }} />
                  Manager
                </NavButton>
              )}
              
              {auth?.type === "Founder" && (
                <NavButton
                  isActive={location?.pathname?.includes('founder')}
                  onClick={() => navigate('/dashboard/manager/introductions')}
                >
                  <Briefcase style={{ width: '1rem', height: '1rem' }} />
                  Founder
                </NavButton>
              )}
            </div>
          </div>

          <div style={styles.userSection}>
            {auth?.email && (
              <ProfileDropdown auth={auth} onLogout={handleLogout} />
            )}
          </div>
        </div>
      </div>

      <div className="show-on-mobile" style={styles.mobileNav}>
        <div style={styles.mobileNavLinks}>
          {navItems.map(({ label, icon: Icon, path }) => {
            const fullPath = `/dashboard/${path}`;
            const isActive = location?.pathname === fullPath;
            
            return (
              <NavButton
                key={path}
                isActive={isActive}
                onClick={() => navigate(fullPath)}
              >
                <Icon style={{ width: '1rem', height: '1rem' }} />
                {label}
              </NavButton>
            );
          })}
        </div>
      </div>
    </nav>
  );
});

export default Navbar;
